import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Settings, BarChart3 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { checkSubscription } from '../services/subscriptionService';
import { Calendar as CalendarType } from '../types/calendar';
import { getBannerStyle } from '../lib/banner/utils';
import CalendarGrid from '../components/calendar/CalendarGrid';
import CalendarLinks from '../components/calendar/CalendarLinks';
import CalendarHeader from '../components/calendar/CalendarHeader';
import CalendarVideo from '../components/calendar/CalendarVideo';
import CalendarVideoButton from '../components/calendar/CalendarVideoButton';
import PrivateCalendarShare from '../components/calendar/PrivateCalendarShare';
import CountryFilter from '../components/calendar/CountryFilter';
import SourceFilter from '../components/calendar/SourceFilter';
import SourceBadges from '../components/calendar/SourceBadges';
import ShareCalendarModal from '../components/settings/ShareCalendarModal';
import StatsModal from '../components/modals/StatsModal';
import SEO from '../components/SEO';
import { toast } from '../utils/toast';
import { getGoogleCalendarSubscribeUrl } from '../utils/calendarUrl';
import CategoryBadge from '../components/CategoryBadge';
import ShareButton from '../components/ShareButton';
import CalendarPricing from '../components/calendar/CalendarPricing';
import CalendarDescription from '../components/calendar/CalendarDescription';
import { useGoogleCalendar } from '../hooks/useGoogleCalendar';
import CalendarFooter from '../components/calendar/CalendarFooter';
import Navigation from '../components/layout/Navigation';
import EmailVerificationBanner from '../components/notifications/EmailVerificationBanner';

function Calendar() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [calendar, setCalendar] = useState<CalendarType | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [showStats, setShowStats] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);

  const { events: calendarEvents } = useGoogleCalendar(calendar?.google_calendar_url || '');

  const pageTitle = calendar?.name || 'Calendar';
  const pageDescription = calendar ? `View and subscribe to ${pageTitle}. ${subscriberCount} subscriber${subscriberCount !== 1 ? 's' : ''}.` : 'Calendar';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Handle Stripe success
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && calendar?.google_calendar_url) {
      // Clear the session_id from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('session_id');
      navigate(`${location.pathname}${newParams.toString() ? `?${newParams.toString()}` : ''}`, { replace: true });
      
      // Show success message
      toast.success('Payment processed successfully!');
      
      // Open Google Calendar subscription in new tab
      window.open(getGoogleCalendarSubscribeUrl(calendar.google_calendar_url), '_blank');
      
      // Refresh calendar data to update subscription status
      if (id) {
        fetchCalendarData();
      }
    }
  }, [searchParams, navigate, location.pathname, calendar?.google_calendar_url]);

  useEffect(() => {
    if (id) {
      fetchCalendarData();
    }
  }, [id, user?.id]);

  const fetchCalendarData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      console.log('Calendar - Fetching data:', {
        calendarId: id,
        userId: user?.id
      });

      // Fetch calendar data and subscriber count in parallel
      const [calendarResponse, statsResponse] = await Promise.all([
        supabase
          .from('calendars')
          .select('*, profiles!calendars_user_id_fkey(display_name)')
          .eq('id', id)
          .single(),
        supabase
          .from('calendar_stats')
          .select('subscriber_count')
          .eq('calendar_id', id)
          .single()
      ]);

      if (calendarResponse.error) {
        console.error('Calendar fetch error:', calendarResponse.error);
        setError('Calendar not found');
        return;
      }

      console.log('Calendar - Data fetched:', {
        calendar: calendarResponse.data,
        stats: statsResponse.data
      });

      setCalendar(calendarResponse.data);
      setSubscriberCount(statsResponse.data?.subscriber_count || 0);

      if (user) {
        const subscribed = await checkSubscription(id, user.id);
        console.log('Calendar - Subscription check:', {
          isSubscribed: subscribed
        });
        setIsSubscribed(subscribed);
      }
    } catch (err) {
      console.error('Error fetching calendar:', err);
      setError('Failed to load calendar data');
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  if (initialLoad && loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <SEO title={pageTitle} noindex={true} />
        <div className="text-gray-600">Loading calendar...</div>
      </div>
    );
  }

  if (error || !calendar) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <SEO title="Calendar Not Found" noindex={true} />
        <div className="text-gray-600">{error || 'Calendar not found'}</div>
      </div>
    );
  }

  const bannerStyle = getBannerStyle(calendar?.banner);

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <EmailVerificationBanner />
      <SEO
        title={pageTitle}
        description={pageDescription}
        type="article"
        url={window.location.href}
        image={calendar?.banner?.image || 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?auto=format&fit=crop&w=1200&h=630&q=80'}
        isCalendarPage={true}
      />
      
      <div className="relative min-h-screen">
        <div 
          className="absolute inset-0 h-[800px]"
          style={bannerStyle}
        />
        
        <div className="relative">
          <div className="w-full px-4 sm:px-6 lg:px-8 pt-24 pb-16">
            <div className="max-w-7xl mx-auto">
              <div className="text-center space-y-6">
                <h1
                  className="text-6xl font-bold"
                  style={{ color: bannerStyle.color }}
                >
                  {calendar.name}
                </h1>

                <SourceBadges
                  events={calendarEvents}
                  calendarId={calendar.id}
                  color={bannerStyle.color}
                />

                <div className="space-y-4">
                  {calendar.profiles?.display_name && (
                    <p
                      className="text-xl opacity-75"
                      style={{ color: bannerStyle.color }}
                    >
                      Created by {calendar.profiles.display_name}
                    </p>
                  )}

                  <div className="flex items-center justify-center space-x-4">
                    {calendar.category_id && (
                      <CategoryBadge categoryId={calendar.category_id} size="lg" />
                    )}

                    <CalendarPricing
                      paymentType={calendar.payment_type as 'free' | 'subscription' | 'one_time'}
                      subscriptionPriceCents={calendar.subscription_price_cents}
                      eventPriceCents={calendar.event_price_cents}
                    />
                  </div>

                  <div className="flex items-center justify-center space-x-4">
                    <div 
                      className="flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm font-medium bg-white/10 backdrop-blur-sm"
                      style={{ color: bannerStyle.color }}
                    >
                      <span>{subscriberCount} subscribers</span>
                    </div>

                    <ShareButton url={window.location.href} />
                  </div>

                  {/* Owner Actions */}
                  {user?.id === calendar.user_id && (
                    <div className="flex justify-center gap-3">
                      <button
                        onClick={() => setShowStats(true)}
                        className="p-2.5 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors"
                        style={{ color: bannerStyle.color }}
                        title="View Statistics"
                      >
                        <BarChart3 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => navigate(`/calendar/${calendar.id}/settings`)}
                        className="p-2.5 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors"
                        style={{ color: bannerStyle.color }}
                        title="Calendar Settings"
                      >
                        <Settings className="w-5 h-5" />
                      </button>
                    </div>
                  )}

                  <CalendarDescription 
                    description={calendar.description}
                    physicalAddress={calendar.physical_address}
                    addressVisibility={calendar.address_visibility}
                    isSubscribed={isSubscribed}
                    subscriptionLimit={calendar.subscription_limit}
                    subscriberCount={subscriberCount}
                  />
                </div>
              </div>

              {calendar.demo_video_url && (
                <div className="mt-4 flex justify-center">
                  <CalendarVideoButton 
                    onClick={() => setShowVideo(true)} 
                    color={bannerStyle.color}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
            {/* Show private calendar sharing section for owners */}
            {user?.id === calendar.user_id && !calendar.is_public && (
              <PrivateCalendarShare 
                calendar={calendar}
                onInvite={() => setShowShareModal(true)}
              />
            )}

            <div className="bg-white rounded-lg shadow-xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-purple-100">
                <div className="order-1 lg:order-2 p-6">
                  <CalendarLinks
                    calendar={calendar}
                    isSubscribed={isSubscribed}
                    onSubscriptionChange={fetchCalendarData}
                    subscriberCount={subscriberCount}
                  />
                  <CountryFilter
                    events={calendarEvents}
                    calendarId={calendar.id}
                    selectedCountries={selectedCountries}
                    onSelectionChange={setSelectedCountries}
                  />
                  <SourceFilter
                    events={calendarEvents}
                    calendarId={calendar.id}
                    selectedSources={selectedSources}
                    onSelectionChange={setSelectedSources}
                  />
                </div>
                <div className="order-2 lg:order-1 lg:col-span-2">
                  <CalendarGrid
                    googleCalendarUrl={calendar.google_calendar_url}
                    isSubscribed={isSubscribed}
                    calendarId={calendar.id}
                    isPublic={calendar.is_public}
                    paymentType={calendar.payment_type as 'free' | 'subscription' | 'one_time'}
                    addressVisibility={calendar.address_visibility}
                    selectedCountries={selectedCountries}
                    selectedSources={selectedSources}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <StatsModal
          calendarId={calendar.id}
          isOpen={showStats}
          onClose={() => setShowStats(false)}
        />

        {calendar.demo_video_url && (
          <CalendarVideo
            videoUrl={calendar.demo_video_url}
            isOpen={showVideo}
            onClose={() => setShowVideo(false)}
            calendarName={calendar.name}
          />
        )}

        <ShareCalendarModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          calendar={calendar}
        />
      </div>

      <CalendarFooter />
    </div>
  );
}

export default Calendar;